/**
 * The revision compare (APP_SPEC box 5), worn in Ant Design: two
 * pickers over the project's saved revisions (snapshots are plain
 * projects, so the diff engine compares them unchanged), then the
 * four-bucket accounting -- totals, per-class rows, and the changed
 * element ids.
 */
import { useMemo, useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import { Alert, Select, Space, Statistic, Table, Typography } from 'antd';
import type { ElementFilter } from '../../../api';
import { INK } from '../../../theme';
import { DELTA } from '../../../strata';
import { useRevisionList, useVersionDiff, type DiffBucketCounts } from '../queries';

const BUCKETS: { key: keyof DiffBucketCounts; label: string; color: string }[] = [
  { key: 'added', label: 'Added', color: DELTA.added },
  { key: 'modified', label: 'Modified', color: DELTA.modified },
  { key: 'deleted', label: 'Removed', color: DELTA.removed },
  { key: 'unchanged', label: 'Unchanged', color: DELTA.unchanged },
];

const ID_PREVIEW_LIMIT = 12;

function IdList({ label, ids, color }: { label: string; ids: string[]; color: string }) {
  if (ids.length === 0) return null;
  return (
    <div>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color, marginBottom: 4 }}>
        {label} ({ids.length})
      </div>
      <Typography.Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
        {ids.slice(0, ID_PREVIEW_LIMIT).join('  ')}
        {ids.length > ID_PREVIEW_LIMIT && `  … +${ids.length - ID_PREVIEW_LIMIT} more`}
      </Typography.Text>
    </div>
  );
}

export function DiffPanel({ projectId }: { projectId: string }) {
  const { data: revisions = [] } = useRevisionList(projectId);
  const filter = useSearch({ strict: false }) as ElementFilter;

  // Pickers hold snapshot project ids; '' = the LIVE project. Default:
  // latest snapshot vs live -- "what changed since I saved".
  const [basePick, setBasePick] = useState<string | null>(null);
  const [revisedPick, setRevisedPick] = useState<string | null>(null);
  const base = basePick ?? revisions[0]?.snapshot_project_id ?? '';
  const revised = revisedPick ?? '';
  const baseId = base || projectId;
  const revisedId = revised || projectId;

  const { data, isError } = useVersionDiff(baseId, revisedId);
  const result = data && !data.queued ? data.result : null;

  const classRows = useMemo(() => {
    if (!result) return [];
    const rows = Object.entries(result.by_class).map(([cls, counts]) => ({ cls, ...counts }));
    return filter.ifc_class ? rows.filter((row) => row.cls === filter.ifc_class) : rows;
  }, [result, filter.ifc_class]);

  const options = [
    { value: '', label: 'Live' },
    ...revisions.map((rev) => ({
      value: rev.snapshot_project_id,
      label: `r${rev.number}${rev.label ? ` — ${rev.label}` : ''}`,
    })),
  ];

  if (revisions.length === 0) {
    return (
      <Typography.Text type="secondary">
        No revisions yet — save one in the viewer first; diffs compare saved snapshots
        (and the live model).
      </Typography.Text>
    );
  }

  const columns = [
    { title: 'Class', dataIndex: 'cls' },
    ...BUCKETS.map((bucket) => ({
      title: bucket.label,
      key: bucket.key,
      width: 110,
      render: (row: DiffBucketCounts & { cls: string }) => (
        <span style={{ color: row[bucket.key] > 0 ? bucket.color : INK.faint }}>
          {row[bucket.key]}
        </span>
      ),
    })),
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Space>
        <Select value={base} onChange={setBasePick} options={options} style={{ minWidth: 160 }} />
        <Typography.Text type="secondary">→</Typography.Text>
        <Select value={revised} onChange={setRevisedPick} options={options} style={{ minWidth: 160 }} />
      </Space>
      {baseId === revisedId && (
        <Typography.Text type="secondary">Pick two different versions.</Typography.Text>
      )}
      {isError && <Alert type="error" showIcon title="Diff failed to load." />}
      {data?.queued && (
        <Typography.Text type="secondary">
          Diff is materializing on the worker — this page updates itself.
        </Typography.Text>
      )}
      {result && (
        <>
          <Space size="large">
            {BUCKETS.map((bucket) => (
              <Statistic
                key={bucket.key}
                title={bucket.label.toLowerCase()}
                value={result.totals[bucket.key]}
                valueStyle={{ color: bucket.color, fontSize: 20 }}
              />
            ))}
          </Space>
          <Table
            rowKey="cls"
            size="small"
            columns={columns}
            dataSource={classRows}
            pagination={false}
          />
          <IdList label="Added" ids={result.added} color={DELTA.added} />
          <IdList label="Modified" ids={result.modified} color={DELTA.modified} />
          <IdList label="Removed" ids={result.deleted} color={DELTA.removed} />
        </>
      )}
    </Space>
  );
}
